import { apiRequestWithHeaders } from "../client";
import {
    type PDFGenerationParams,
    type BatchPDFRequest,
    type BatchPDFProgress,
    type BatchPDFResult,
    type BatchPDFAsyncStartResponse,
    type BatchPDFAsyncStatusResponse,
    type BatchPDFTaskListResponse,
    type BatchPDFTaskStatus,
    type SinglePDFAsyncRequest,
    getResultDetails,
} from "./types";
import {
    downloadFromBlobUrl,
    filenameFromContentDisposition,
    getPdfDownloadErrorMessage,
} from "@/utils/pdfBlobDownload";

function headerAsString(value: unknown): string {
    if (value == null) return "";
    if (Array.isArray(value)) return value.map(String).join(", ");
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return "";
}

/**
 * BatchPDFService
 * 
 * Service class for batch PDF generation operations.
 * Handles both synchronous and asynchronous PDF generation,
 * task tracking, validation, and recommendations.
 * 
 * Features:
 * - Synchronous batch generation (waits for completion)
 * - Asynchronous batch generation (background processing)
 * - Task status polling
 * - Task listing and filtering
 * - Request validation
 * - Batch size recommendations
 * 
 * @example
 * ```typescript
 * import { batchPDFService } from '@/services/api/pdf';
 * 
 * // Start async generation
 * const response = await batchPDFService.startAsyncBatch({
 *   pdfs: [...],
 *   filename: 'my_reports',
 *   include_manifest: true
 * });
 * 
 * // Poll for status
 * const status = await batchPDFService.getTaskStatus(response.task_id);
 * 
 * // List all tasks
 * const tasks = await batchPDFService.listTasks('processing', 50, 0);
 * ```
 */
export class BatchPDFService {
    /**
     * Generate multiple PDFs and bundle them into a ZIP archive
     *
     * @param request - The batch PDF generation request
     * @param onProgress - Optional progress callback
     * @returns Promise that resolves to the generation result
     */
    async generateBatch(
        request: BatchPDFRequest,
        onProgress?: (progress: BatchPDFProgress) => void
    ): Promise<BatchPDFResult> {
        try {
            onProgress?.({
                phase: "preparing",
                message: "Preparing PDF generation request...",
                progress: 0,
            });

            const response = await apiRequestWithHeaders<Blob>({
                method: "POST",
                url: "/pdf/generate-batch",
                data: request,
                responseType: "blob",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/zip, application/pdf",
                },
            });

            onProgress?.({
                phase: "generating",
                message: `Generating ${request.pdfs.length} PDF${request.pdfs.length > 1 ? "s" : ""
                    }...`,
                progress: 50,
            });

            const contentType = headerAsString(response.headers["content-type"]);
            const isPdf = contentType.includes("application/pdf");
            const documentCount = parseInt(
                headerAsString(response.headers["x-document-count"]) || "0",
                10
            );
            const failedCount = parseInt(
                headerAsString(response.headers["x-failed-count"]) || "0",
                10
            );
            const generationTime = headerAsString(response.headers["x-generation-time"]);

            onProgress?.({
                phase: "downloading",
                message: "Downloading generated files...",
                progress: 75,
            });

            const blob = response.data;

            // Determine filename
            const contentDisposition = headerAsString(response.headers["content-disposition"]);
            const serverFilename = filenameFromContentDisposition(
                contentDisposition,
                `${request.filename || "documents"}.${isPdf ? "pdf" : "zip"}`,
            );

            // Trigger browser download
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = serverFilename;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            window.URL.revokeObjectURL(url);

            onProgress?.({
                phase: "complete",
                message: "Download complete",
                progress: 100,
            });

            // Log success metrics
            console.log("PDF generation completed:", {
                documentCount,
                failedCount,
                fileType: isPdf ? "pdf" : "zip",
                filename: serverFilename,
                generationTime,
            });

            if (failedCount > 0) {
                console.warn(`${failedCount} PDFs failed to generate`);
            }

            return {
                success: true,
                fileType: isPdf ? "pdf" : "zip",
                filename: serverFilename,
                documentCount,
                failedCount: failedCount || undefined,
                generationTime: generationTime || undefined,
            };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "PDF generation failed";

            onProgress?.({
                phase: "error",
                message: errorMessage,
                progress: 0,
            });

            console.error("Error generating PDFs:", error);
            throw error;
        }
    }

    /**
     * Check the health of the PDF generation service
     */
    async healthCheck(): Promise<{ status: string; service: string }> {
        try {
            const response = await apiRequestWithHeaders<{ status: string; service: string }>({
                method: "GET",
                url: "/pdf/health",
            });
            return response.data;
        } catch (error) {
            console.error("PDF service health check failed:", error);
            throw error;
        }
    }

    /**
     * Validate batch size and parameters
     */
    validateBatchRequest(request: BatchPDFRequest): string | null {
        if (!request.pdfs || request.pdfs.length === 0) {
            return "Please select at least one PDF to generate";
        }

        if (request.pdfs.length > 200) {
            return "Maximum batch size is 200 PDFs. Please reduce your selection.";
        }

        // Check for duplicate IDs
        const ids = request.pdfs.map((p) => p.id);
        const uniqueIds = new Set(ids);
        if (ids.length !== uniqueIds.size) {
            return "Duplicate PDF IDs detected. Each PDF must have a unique ID.";
        }

        // Validate required fields
        for (const pdf of request.pdfs) {
            if (!pdf.id || pdf.id.trim() === "") {
                return "All PDFs must have a valid ID";
            }
        }

        return null;
    }

    /**
     * Get recommended batch size information
     */
    getRecommendedBatchSize(pdfCount: number): {
        recommended: boolean;
        message?: string;
    } {
        if (pdfCount <= 50) {
            return { recommended: true };
        }

        if (pdfCount <= 100) {
            return {
                recommended: true,
                message:
                    "Large batch detected. Generation may take 2-3 minutes. Consider splitting into smaller batches for faster results.",
            };
        }

        return {
            recommended: false,
            message:
                "Very large batch. We recommend splitting into batches of 50 PDFs or less for optimal performance.",
        };
    }

    /**
     * Start an async batch PDF generation task (background processing)
     * 
     * @param request - The batch PDF generation request
     * @returns Promise that resolves to the task information
     */
    async startAsyncBatch(request: BatchPDFRequest): Promise<BatchPDFAsyncStartResponse> {
        try {
            const response = await apiRequestWithHeaders<BatchPDFAsyncStartResponse>({
                method: "POST",
                url: "/pdf/generate-batch-async",
                data: request,
                headers: {
                    "Content-Type": "application/json",
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error starting async batch PDF generation:", error);
            throw error;
        }
    }

    /**
     * Start async single PDF generation (background worker task).
     */
    async startAsyncSingle(request: SinglePDFAsyncRequest): Promise<BatchPDFAsyncStartResponse> {
        try {
            const response = await apiRequestWithHeaders<BatchPDFAsyncStartResponse>({
                method: "POST",
                url: "/pdf/generate-async",
                data: request,
                headers: {
                    "Content-Type": "application/json",
                },
            });

            return response.data;
        } catch (error) {
            console.error("Error starting async single PDF generation:", error);
            throw error;
        }
    }

    /**
     * Get the status of an async batch PDF generation task
     * 
     * @param taskId - The task ID to check
     * @returns Promise that resolves to the task status
     */
    async getTaskStatus(taskId: string): Promise<BatchPDFAsyncStatusResponse> {
        try {
            const response = await apiRequestWithHeaders<BatchPDFAsyncStatusResponse>({
                method: "GET",
                url: `/pdf/task-status/${taskId}`,
            });

            return response.data;
        } catch (error) {
            console.error("Error getting task status:", error);
            throw error;
        }
    }

    /**
     * List all tasks for the current user
     * 
     * @param status - Optional status filter
     * @param limit - Max results (default: 50)
     * @param offset - Pagination offset (default: 0)
     * @param since - Optional ISO timestamp - only tasks created after this time
     * @returns Promise that resolves to the task list
     */
    async listTasks(
        status?: BatchPDFTaskStatus,
        limit: number = 50,
        offset: number = 0,
        since?: string
    ): Promise<BatchPDFTaskListResponse> {
        try {
            const params = new URLSearchParams();
            if (status) params.append('status', status);
            params.append('limit', limit.toString());
            params.append('offset', offset.toString());
            if (since) params.append('since', since);

            const response = await apiRequestWithHeaders<BatchPDFTaskListResponse>({
                method: "GET",
                url: `/pdf/tasks?${params.toString()}`,
            });

            return response.data;
        } catch (error) {
            console.error("Error listing tasks:", error);
            throw error;
        }
    }

    /**
     * Download a completed task file via the auth-gated API endpoint.
     */
    async downloadTaskFile(taskId: string, filename: string): Promise<void> {
        const response = await apiRequestWithHeaders<Blob>({
            method: "GET",
            url: `/pdf/tasks/${taskId}/download`,
            responseType: "blob",
        });

        const contentDisposition = headerAsString(response.headers["content-disposition"]);
        const downloadFilename = filenameFromContentDisposition(contentDisposition, filename);

        const url = window.URL.createObjectURL(response.data);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = downloadFilename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
    }

    /**
     * Download a task result — uses auth-gated API for new tasks, legacy SAS for old tasks.
     */
    async downloadTaskResult(
        taskId: string,
        result: BatchPDFAsyncStatusResponse["result"] | BatchPDFTaskListResponse["tasks"][number]["result"],
        fallbackFilename: string,
    ): Promise<void> {
        const details = getResultDetails(result);
        const filename = details?.filename || fallbackFilename;

        if (details?.download_url) {
            await downloadFromBlobUrl(details.download_url, filename);
            return;
        }

        await this.downloadTaskFile(taskId, filename);
    }

    /**
     * Poll for task completion with automatic retries
     * 
     * @param taskId - The task ID to poll
     * @param onProgress - Optional progress callback
     * @param pollInterval - Polling interval in milliseconds (default: 2000)
     * @param maxAttempts - Maximum polling attempts (default: 300 = 10 minutes at 2s intervals)
     * @returns Promise that resolves when task is complete
     */
    async pollTaskStatus(
        taskId: string,
        onProgress?: (status: BatchPDFAsyncStatusResponse) => void,
        pollInterval: number = 2000,
        maxAttempts: number = 300
    ): Promise<BatchPDFAsyncStatusResponse> {
        let attempts = 0;

        return new Promise((resolve, reject) => {
            const poll = async () => {
                try {
                    attempts++;

                    if (attempts > maxAttempts) {
                        reject(new Error("Task polling timeout - maximum attempts reached"));
                        return;
                    }

                    const status = await this.getTaskStatus(taskId);
                    onProgress?.(status);

                    if (status.status === "completed" || status.status === "success") {
                        resolve(status);
                    } else if (status.status === "failed" || status.status === "cancelled") {
                        reject(new Error(status.error || "Task failed"));
                    } else {
                        // Continue polling
                        setTimeout(poll, pollInterval);
                    }
                } catch (error) {
                    reject(error);
                }
            };

            poll();
        });
    }

    /**
     * Generate batch PDFs asynchronously with polling and automatic download
     * 
     * @param request - The batch PDF generation request
     * @param onProgress - Optional progress callback
     * @param useTracker - If true, returns task_id for external tracking (default: false)
     * @returns Promise that resolves to the final result or task_id if useTracker is true
     */
    async generateBatchAsync(
        request: BatchPDFRequest,
        onProgress?: (progress: BatchPDFProgress) => void,
        useTracker?: boolean
    ): Promise<BatchPDFResult & { task_id?: string }> {
        try {
            onProgress?.({
                phase: "preparing",
                message: "Starting background PDF generation...",
                progress: 0,
            });

            // Start the async task
            const startResponse = await this.startAsyncBatch(request);
            const taskId = startResponse.task_id;

            // If using external tracker, return task_id immediately
            if (useTracker) {
                return {
                    success: true,
                    fileType: "zip",
                    filename: request.filename || "documents",
                    documentCount: 0,
                    task_id: taskId,
                };
            }

            onProgress?.({
                phase: "generating",
                message: `Generating ${request.pdfs.length} PDF${request.pdfs.length > 1 ? "s" : ""} in background...`,
                progress: 10,
            });

            // Poll for completion
            const finalStatus = await this.pollTaskStatus(
                taskId,
                (status) => {
                    // Convert task status to progress callback
                    onProgress?.({
                        phase: "generating",
                        message: `Progress: ${status.completed_pdfs}/${status.total_pdfs} PDFs completed`,
                        progress: Math.min(10 + (status.progress_percentage * 0.8), 90),
                    });
                }
            );

            onProgress?.({
                phase: "downloading",
                message: "Downloading generated files...",
                progress: 95,
            });

            const details = getResultDetails(finalStatus.result);

            await this.downloadTaskResult(taskId, finalStatus.result, request.filename || "documents");

            onProgress?.({
                phase: "complete",
                message: "Download complete",
                progress: 100,
            });

            return {
                success: true,
                fileType: "zip",
                filename: details?.filename || request.filename || "documents",
                documentCount: details?.successful_pdfs ?? finalStatus.completed_pdfs,
                failedCount: details?.failed_pdfs && details.failed_pdfs > 0 ? details.failed_pdfs : undefined,
                generationTime: details?.generation_time_seconds ? `${details.generation_time_seconds}s` : undefined,
                task_id: taskId,
            };
        } catch (error) {
            const errorMessage = getPdfDownloadErrorMessage(error);

            onProgress?.({
                phase: "error",
                message: errorMessage,
                progress: 0,
            });

            console.error("Error in async batch generation:", error);
            throw error;
        }
    }
}

// Export singleton instance
export const batchPDFService = new BatchPDFService();
